import { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGetStatsGroupsInfiniteQuery } from "../../query/statsQuery";
import { useStatsScope } from "../../hooks/useStatsScope";
import { formatMoney } from "../../utils/formatMoney";
import { Card } from "../../components/Card";
import { Container } from "../../components/Container";
import { LoadingContainer } from "../../components/LoadingContainer";
import { LoadMoreButton } from "../../components/LoadMoreButton";
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from "../../components/Table";
import { Button } from "../../components/Button";
import { SelectInput } from "../../components/SelectInput";
import type { StatsPeriod, StatsOrderBy } from "../../api/statsApi";
import styled from "styled-components";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import colors from "@styling/colors";

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing.x3}px;
  flex-wrap: wrap;
  gap: ${spacing.x2}px;
`;

const Title = styled.h1`
  font-size: ${fontSizes.xlarge}px;
  font-weight: 700;
  color: ${colors.text};
  margin: 0;
`;

const ErrorBox = styled.div`
  padding: ${spacing.x3}px;
  text-align: center;
  color: ${colors.danger};
`;

const TableBox = styled(Card)`
  background-color: ${colors.background};
  margin-bottom: ${spacing.x2}px;
`;

export const StatsGroupsView = () => {
  const { t } = useTranslation();
  const scope = useStatsScope();
  const [searchParams] = useSearchParams();
  const storeId = searchParams.get("store") ?? undefined;
  const { period, periodId } = useParams<{
    period: StatsPeriod;
    periodId: string;
  }>();
  const [orderBy, setOrderBy] = useState<StatsOrderBy>("quantity");

  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetStatsGroupsInfiniteQuery(
    scope ?? undefined,
    period as StatsPeriod,
    periodId,
    orderBy,
    storeId
  );

  const items = useMemo(() => {
    const pages = data?.pages ?? [];
    return pages.flatMap((page) =>
      page.map((item) => ({
        id: item.groupId,
        name: item.name ?? item.groupId,
        quantity: item.quantity,
        grossAmount: item.grossAmount,
      }))
    );
  }, [data]);

  if (!scope || !period || !periodId) {
    return null;
  }

  if (isLoading && !data) {
    return <LoadingContainer message={t("statistics.loading")} />;
  }

  if (isError) {
    return (
      <Container>
        <ErrorBox>
          <p>{t("statistics.couldNotLoadReport")}</p>
          <Button onClick={() => refetch()}>{t("statistics.retry")}</Button>
        </ErrorBox>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>{t("statistics.mostSoldGroups")}</Title>
        <SelectInput
          value={orderBy}
          onChange={(e) =>
            setOrderBy(e.target.value as StatsOrderBy)
          }
          style={{ width: "auto", minWidth: 140 }}
        >
          <option value="quantity">
            {t("statistics.orderByQuantity")}
          </option>
          <option value="grossAmount">
            {t("statistics.orderByAmount")}
          </option>
        </SelectInput>
      </Header>
      <TableBox>
        <Table>
        <TableHeader>
          <tr>
            <TableHeaderCell>{t("statistics.groupsColumnName")}</TableHeaderCell>
            <TableHeaderCell align="right">
              {t("statistics.receiptTypesColumnCount")}
            </TableHeaderCell>
            <TableHeaderCell align="right">
              {t("statistics.receiptTypesColumnAmount")}
            </TableHeaderCell>
          </tr>
        </TableHeader>
        <TableBody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={3} style={{ padding: spacing.x3, textAlign: "center" }}>
                {t("statistics.noGroupsSold")}
              </td>
            </tr>
          ) : (
            items.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.name}</TableCell>
                <TableCell align="right">{item.quantity}</TableCell>
                <TableCell align="right">
                  {formatMoney(item.grossAmount)}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      </TableBox>
      <LoadMoreButton
        onClick={() => fetchNextPage()}
        isLoading={isFetchingNextPage}
        hasMore={!!hasNextPage}
      />
    </Container>
  );
};
